import { useCallback, useEffect, useState } from 'react';
import { HardDrive, RefreshCw, Trash2, Folder, File, ChevronRight, AlertTriangle } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { Modal } from '../components/ui/modal';
import { S3Bucket, S3Object } from '../models';
import { getS3Buckets, getS3BucketObjects, deleteS3Object, getS3Stats, getApiError } from '../services/api';
import { cn } from '../lib/utils';

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

export const S3StoragePage = () => {
  const [buckets, setBuckets] = useState<S3Bucket[]>();
  const [selectedBucket, setSelectedBucket] = useState<string>();
  const [objects, setObjects] = useState<S3Object[]>([]);
  const [prefix, setPrefix] = useState('');
  const [stats, setStats] = useState<{ bucketCount: number; totalObjects: number }>();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<{ bucket: string; key: string }>();
  const [loadingObjects, setLoadingObjects] = useState(false);

  const loadBuckets = useCallback(async () => {
    setError('');
    try {
      const [bucketRes, statsRes] = await Promise.all([
        getS3Buckets().catch(() => null),
        getS3Stats().catch(() => null),
      ]);
      if (bucketRes) setBuckets(bucketRes.data.data);
      if (statsRes) setStats(statsRes.data.data);
    } catch (e) {
      setError(getApiError(e));
    }
  }, []);

  useEffect(() => { loadBuckets(); }, [loadBuckets]);

  const loadObjects = async (bucket: string, pfx: string) => {
    setLoadingObjects(true);
    setError('');
    try {
      const response = await getS3BucketObjects(bucket, pfx);
      setSelectedBucket(bucket);
      setPrefix(pfx);
      setObjects(response.data.data.objects);
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setLoadingObjects(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setError(''); setSuccess('');
    try {
      await deleteS3Object(confirmDelete.bucket, confirmDelete.key);
      setSuccess(`Объект "${confirmDelete.key}" удалён`);
      setConfirmDelete(undefined);
      if (selectedBucket) loadObjects(selectedBucket, prefix);
    } catch (e) {
      setError(getApiError(e));
    }
  };

  const navigateFolder = (folder: string) => {
    if (selectedBucket) loadObjects(selectedBucket, folder);
  };

  const breadcrumbs = prefix ? prefix.split('/').filter(Boolean) : [];

  return (
    <AppLayout title="S3 Storage">
      <PageHeader
        title="S3 / MinIO Storage"
        description="Просмотр бакетов, файлов и управление объектами."
        action={<Button variant="outline" onClick={() => { loadBuckets(); if (selectedBucket) loadObjects(selectedBucket, prefix); }}><RefreshCw className="h-4 w-4" /> Обновить</Button>}
      />
      {error && <div className="mb-4 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {success && <div className="mb-4 rounded-xl border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">{success}</div>}

      {!buckets && !error ? <LoadingState /> : (
        <div className="space-y-6">
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Бакетов</span>
                </div>
                <p className="mt-2 text-xl font-bold">{stats.bucketCount}</p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center gap-2">
                  <File className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Объектов</span>
                </div>
                <p className="mt-2 text-xl font-bold">{stats.totalObjects}</p>
              </div>
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
            {/* Bucket List */}
            <div className="rounded-2xl border border-border bg-card shadow-sm">
              <div className="border-b border-border p-4">
                <h2 className="text-sm font-bold">Бакеты</h2>
              </div>
              <div className="p-2">
                {buckets && buckets.map((b) => (
                  <button
                    key={b.name}
                    onClick={() => loadObjects(b.name, '')}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                      selectedBucket === b.name
                        ? 'bg-primary/10 text-primary'
                        : 'text-foreground hover:bg-muted'
                    )}
                  >
                    <Folder className="h-4 w-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{b.name}</p>
                      <p className="text-xs text-muted-foreground">{b.objects} объектов</p>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                ))}
              </div>
            </div>

            {/* Object Browser */}
            <div className="rounded-2xl border border-border bg-card shadow-sm">
              <div className="border-b border-border p-4">
                {selectedBucket ? (
                  <div>
                    <div className="flex items-center gap-2 text-sm">
                      <button onClick={() => loadObjects(selectedBucket, '')} className="font-semibold hover:text-primary">{selectedBucket}</button>
                      {breadcrumbs.map((crumb, i) => (
                        <span key={i} className="flex items-center gap-2">
                          <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          <button
                            onClick={() => navigateFolder(breadcrumbs.slice(0, i + 1).join('/') + '/')}
                            className="text-muted-foreground hover:text-primary"
                          >
                            {crumb}
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Выберите бакет для просмотра</p>
                )}
              </div>

              <div className="max-h-[600px] overflow-y-auto">
                {!selectedBucket ? (
                  <div className="flex flex-col items-center gap-3 py-20 text-center">
                    <HardDrive className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Выберите бакет слева</p>
                  </div>
                ) : loadingObjects ? <LoadingState /> : objects.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 py-20 text-center">
                    <Folder className="h-10 w-10 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Папка пуста</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {objects.map((obj) => {
                      const isFolder = obj.key.endsWith('/');
                      const name = obj.key.split('/').filter(Boolean).pop() || obj.key;
                      return (
                        <div key={obj.key} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40">
                          {isFolder ? (
                            <button onClick={() => navigateFolder(obj.key)} className="flex items-center gap-3 text-foreground hover:text-primary">
                              <Folder className="h-4 w-4 text-warning" />
                              <span className="text-sm font-medium">{name}</span>
                            </button>
                          ) : (
                            <>
                              <File className="h-4 w-4 text-muted-foreground" />
                              <span className="flex-1 text-sm font-mono">{name}</span>
                              <span className="text-xs text-muted-foreground">{formatBytes(obj.size)}</span>
                              <button onClick={() => setConfirmDelete({ bucket: selectedBucket, key: obj.key })} className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(undefined)} title="Удаление объекта" maxWidth="max-w-md"
        footer={<>
          <Button variant="ghost" onClick={() => setConfirmDelete(undefined)}>Отмена</Button>
          <Button variant="destructive" onClick={handleDelete}>Удалить</Button>
        </>}
      >
        <div className="flex gap-3 rounded-xl border border-warning/25 bg-warning/10 p-4 text-sm">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
          <p>Объект <strong>"{confirmDelete?.key}"</strong> в бакете <strong>"{confirmDelete?.bucket}"</strong> будет удалён безвозвратно.</p>
        </div>
      </Modal>
    </AppLayout>
  );
};
